import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  CardActions,
  CardProps,
  Icon,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import {
  Close as CloseIcon,
  FileCopyOutlined as FileCopyOutlinedIcon
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { VersionItem } from '../../../common/version-item/Version.interface';
import WrappedTableCell from '../../../common/WrappedTableCell';
import AssignDocumentVersionDialog from './AssignDocumentVersionDialog';

const columns: { key: string; label: string }[] = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'version', label: 'Version' },
  { key: 'status', label: 'Status' }
];

export interface DocumentVersionReferenceCardProps {
  items: VersionItem[] | undefined;
  icon?: string;
  title?: string;
  disabled?: boolean;
  readonly?: boolean;
  isLoading?: boolean;
  error?: FetchError;
  direction?: 'ASC' | 'DESC';
  className?: string;
  assignButtonLabel?: string;
  actions?: React.ReactNode;
  cardProps?: CardProps;
  handleSort?: (column: string) => void;
  isActive?: (column: string) => boolean;
  onAssign?: (items: VersionItem[]) => void;
  onRemove?: (item: VersionItem) => void;
}

const DocumentVersionReferenceCard: React.FC<
  DocumentVersionReferenceCardProps
> = props => {
  const {
    items,
    icon,
    title = 'Document References',
    disabled,
    readonly,
    direction,
    isLoading,
    error,
    className,
    assignButtonLabel = 'Assign Documents',
    actions,
    cardProps,
    handleSort,
    isActive,
    onAssign,
    onRemove
  } = props;
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [removeItem, setRemoveItem] = useState<VersionItem>();
  const colSpan = columns.length + 1;

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return isActive?.(column) && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <Card className={className} {...cardProps}>
      <CardTitle>
        {icon ? <Icon>{icon}</Icon> : <FileCopyOutlinedIcon />}
        {title}
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell key={`${column.key}-${index}`}>
                  <TableSortLabel
                    active={isActive?.(column.key)}
                    direction={getSortDirection(column.key)}
                    onClick={() => handleSort?.(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell padding='none' colSpan={colSpan}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {error && (
              <TableRow>
                <TableCell padding='none' colSpan={colSpan}>
                  <Alert severity='error'>{error.message}</Alert>
                </TableCell>
              </TableRow>
            )}
            {(!items || items.length === 0) && (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  {isLoading ? 'Loading...' : 'No linked documents.'}
                </TableCell>
              </TableRow>
            )}
            {items?.map((item, index) => (
              <TableRow
                hover
                color='primary'
                key={`${item.id}-${item.version}-${index}`}
                onClick={() => {
                  if (readonly) {
                    return;
                  }

                  // set version params if navigating previous version
                  const params = item.draft ? '' : `?ver=${item.version}`;
                  history.push(
                    `/portal/documents/file/task/${item.taskId}${params}`
                  );
                }}
              >
                <TableCell>{item.task.key}</TableCell>
                <WrappedTableCell>{item.name}</WrappedTableCell>
                <TableCell>{item.version}</TableCell>
                <TableCell>
                  <TaskStatusLabel task={item.task} />
                </TableCell>
                <TableCell padding='checkbox'>
                  <IconButton
                    disabled={disabled}
                    onClick={event => {
                      event.stopPropagation();
                      !readonly && setRemoveItem(item);
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CardActions className='card-actions'>
        <Button
          color='primary'
          variant='outlined'
          disabled={disabled}
          onClick={() => !readonly && setOpen(true)}
        >
          {assignButtonLabel}
        </Button>
        {actions}
      </CardActions>

      <AssignDocumentVersionDialog
        open={open}
        items={items}
        onCancel={() => setOpen(false)}
        onAssign={items => {
          onAssign?.(items);
          setOpen(false);
        }}
      />
      <ConfirmDialog
        open={!!removeItem}
        title='Unlink Document'
        message={`Are you sure you want to unlink ${
          removeItem?.task.key || '?'
        }?`}
        onCancel={() => setRemoveItem(undefined)}
        onConfirm={() => {
          setRemoveItem(undefined);
          if (removeItem) {
            onRemove?.(removeItem);
          }
        }}
      />
    </Card>
  );
};

export default DocumentVersionReferenceCard;
